import type { BranchSettingsView, UpsertBranchRequest } from "@planerius/contracts/cabinet"

import { plural } from "../../lib/plural"

/**
 * 4-Б1 «Филиал: шаг сетки и режим заполнения» как данные, отдельно от разметки.
 *
 * ШАГ ПОДПИСАН ТЕМ, ЧТО УВИДИТ КЛИЕНТ, а не числом. «15 минут» ничего не говорит
 * владельцу; «клиенту предлагается 10:00, 10:15, 10:30» — говорит всё. Тексты взяты
 * из макета дословно: это решение дизайнера о том, как объяснять настройку.
 *
 * РЕЖИМ ЗАПОЛНЕНИЯ — ТОЖЕ ПРО ПОСЛЕДСТВИЕ. «tight» звучит как ускорение, а означает
 * некруглое время в трубке: «приходите в 14:37». Салон вправе так работать, но выбрать
 * это он должен, прочитав, а не угадав.
 */

export const GRID_STEPS = [5, 10, 15, 20, 30, 60] as const
export type GridStep = (typeof GRID_STEPS)[number]

export const DEFAULT_STEP: GridStep = 15

/** Подписи шагов — дословно из макета 4-Б1. */
export const STEP_TEXT: Readonly<Record<GridStep, string>> = {
  5: "Шаг 5 минут: клиенту предлагается 10:00, 10:05, 10:10. Самая плотная сетка — для услуг на 10 минут.",
  10: "Шаг 10 минут: клиенту предлагается 10:00, 10:10, 10:20.",
  15: "Шаг 15 минут: клиенту предлагается 10:00, 10:15, 10:30. Мелкий шаг нужен, если есть услуги на 10 минут — коррекция бровей, подкраска. Крупный удобнее, если процедуры идут по часу.",
  20: "Шаг 20 минут: клиенту предлагается 10:00, 10:20, 10:40.",
  30: "Шаг 30 минут: клиенту предлагается 10:00, 10:30, 11:00. Удобно косметологии и массажу — день помещается на экран без прокрутки.",
  60: "Шаг 60 минут: клиенту предлагается 10:00, 11:00, 12:00. Только для часовых процедур.",
}

export const PACKING_TEXT: Readonly<Record<"grid" | "tight", { title: string; text: string }>> = {
  grid: {
    title: "По сетке",
    text: "Время предлагается ровно: 10:00, 10:15, 10:30",
  },
  tight: {
    title: "Без окон",
    text: "Следующее свободное — сразу после предыдущей записи. День плотнее, время некруглое",
  },
}

export function stepLabel(step: GridStep): string {
  return `${String(step)} мин${step === DEFAULT_STEP ? " · по умолчанию" : ""}`
}

/**
 * Предупреждение о записях, которые в новую сетку не попадут (4-Б1).
 *
 * Шаг не двигает записи: визит на 14:15 при шаге 30 остаётся на 14:15 и рисуется между
 * линиями. Это ожидаемо, и сказать это надо ДО сохранения — иначе владелец увидит
 * «сломанную» сетку и позвонит в поддержку, а администратор будет объяснять, что так
 * и задумано.
 *
 * «unknown» — ПРЕДПРОСМОТР НЕ ОТВЕТИЛ, и это не то же самое, что «ноль записей».
 * Молчание здесь читается как «всё ровно», а на деле число просто неизвестно: владелец
 * сохранит шаг, увидит записи между линиями и решит, что сетка сломалась.
 *
 * null — предупреждать не о чем: шаг тот же или записей вне сетки нет.
 */
export function offGridWarning(input: {
  readonly currentStep: number
  readonly nextStep: number
  readonly offGrid: number | "unknown"
}): string | null {
  if (input.nextStep === input.currentStep) return null
  if (input.offGrid === "unknown") {
    return "Не удалось посчитать, сколько записей окажется вне новой сетки. Сохранять можно — записи всё равно останутся на своём времени, но сколько их, сейчас неизвестно."
  }
  if (input.offGrid <= 0) return null
  const count = `${String(input.offGrid)} ${plural(input.offGrid, "запись", "записи", "записей")}`
  return `Шаг изменится для нового времени. ${count} стоят на времени, которое в новую сетку не попадает, — они останутся как есть.`
}

/**
 * Текст конфликта версий — с обоими значениями, а не «кто-то изменил» (4-Б1).
 *
 * Человеку предстоит решить, чьё значение оставить, и решить это он может, только видя
 * оба. «Данные изменились, обновите страницу» отправляет его начинать сначала и терять
 * свою правку.
 */
export function conflictText(input: {
  readonly currentStep: number
  readonly wantedStep: number
}): string {
  return `Не удалось сохранить: кто-то изменил настройки филиала, пока вы их правили. Шаг сейчас ${String(input.currentStep)}, вы ставите ${String(input.wantedStep)}. Оставить ваше или взять текущее?`
}

/**
 * Запрос сохранения СОБИРАЕТСЯ ИЗ ЗАГРУЖЕННОГО ФИЛИАЛА ЦЕЛИКОМ.
 *
 * Сохранение филиала — upsert: сервер пишет то, что прислали, и поля, которых в запросе
 * нет, становятся пустыми. Экран показывает шаг и режим, но отправлять обязан ВСЁ, что
 * прочитал, — иначе координаты, телефон и адрес, которых он даже не рисует, исчезнут
 * при первом же сохранении. Ровно это и происходило, пока ответ был `unknown[]`.
 */
export function branchUpsertRequest(
  branch: BranchSettingsView,
  changes: { readonly slotStepMinutes: GridStep; readonly packingMode: "grid" | "tight" },
): UpsertBranchRequest {
  return {
    id: branch.id,
    ...(branch.organizationId === null ? {} : { organizationId: branch.organizationId }),
    name: branch.name,
    timezone: branch.timezone,
    address: branch.address,
    latitude: branch.latitude,
    longitude: branch.longitude,
    phoneE164: branch.phoneE164,
    slotStepMinutes: changes.slotStepMinutes,
    packingMode: changes.packingMode,
    isActive: branch.isActive,
    expectedUpdatedAt: branch.updatedAt,
  }
}
